import React, { useMemo, useState } from 'react';
import { getCardTemplate, getCardTemplateTypes } from '../../config/cardTemplates';
import { CardPreview } from '../PreviewComponents';

/**
 * CardTemplateModal
 * - Similar to FormTypeModal: choose a template first, then create the component
 * - Shows mini previews for each template (re-uses existing CardPreview)
 */
const CardTemplateModal = ({ isOpen, onClose, onSelectTemplate }) => {
  const [selectedKey, setSelectedKey] = useState('');
  const [query, setQuery] = useState('');

  const templates = useMemo(() => {
    const base = [
      { value: '__blank__', label: 'Blank Card' },
      ...getCardTemplateTypes()
    ];
    const q = query.trim().toLowerCase();
    if (!q) return base;
    return base.filter(t => String(t.label || '').toLowerCase().includes(q));
  }, [query]);

  const theme = useMemo(() => ({ colors: { primary: '#3B82F6' } }), []);

  const handleSubmit = () => {
    if (!selectedKey) {
      alert('Please select a card template');
      return;
    }
    onSelectTemplate(selectedKey);
  };

  if (!isOpen) return null;
  console.log("entered---")
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'radial-gradient(1200px 600px at 15% 10%, rgba(99,102,241,0.55), transparent 60%), radial-gradient(900px 500px at 85% 20%, rgba(236,72,153,0.45), transparent 55%), rgba(0,0,0,0.55)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000
    }}>
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '16px',
        padding: '0px',
        maxWidth: '1060px',
        width: '95%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
        border: '1px solid #E5E7EB'
      }}>
        {/* Header */}
        <div style={{
          padding: '18px 20px',
          borderBottom: '1px solid #E5E7EB',
          background: 'linear-gradient(135deg, rgba(99,102,241,0.12), rgba(236,72,153,0.10))'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 10px 25px rgba(102,126,234,0.25)',
                  color: '#fff',
                  fontSize: '20px'
                }}>
                  🃏
                </div>
                <div>
                  <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '900', color: '#111827' }}>
                    Choose Card Template
                  </h2>
                  <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#6B7280' }}>
                    Select a template → edit on left panel → live preview updates instantly.
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                padding: '8px 12px',
                fontSize: '13px',
                backgroundColor: '#FFFFFF',
                border: '1px solid #D1D5DB',
                borderRadius: '10px',
                cursor: 'pointer',
                color: '#6B7280',
                height: '36px'
              }}
            >
              ✕ Close
            </button>
          </div>

          {/* Search */}
          <div style={{ marginTop: '14px', display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <div style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9CA3AF',
                fontSize: '14px'
              }}>
                🔎
              </div>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search templates (pricing, product, neon...)"
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 34px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '12px',
                  fontSize: '13px',
                  backgroundColor: '#FFFFFF'
                }}
              />
            </div>
            <div style={{
              fontSize: '12px',
              color: '#111827',
              backgroundColor: '#FFFFFF',
              padding: '6px 10px',
              borderRadius: '999px',
              fontWeight: '800',
              border: '1px solid #E5E7EB'
            }}>
              {templates.length} options
            </div>
          </div>
        </div>

        {/* Grid */}
        <div style={{
          padding: '18px 20px 4px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: '14px'
        }}>
          {templates.map(t => {
            const isSelected = selectedKey === t.value;
            const template =
              t.value === '__blank__'
                ? null
                : (() => {
                    try { return getCardTemplate(t.value); } catch { return null; }
                  })();

            const previewComponent = {
              id: `preview_${t.value}`,
              type: 'card',
              commonAttrs: template?.commonAttrs || {
                width: '100',
                height: 'auto',
                paddingTop: '16',
                paddingBottom: '16',
                paddingLeft: '16',
                paddingRight: '16',
                marginTop: '0',
                marginBottom: '0',
                marginLeft: '0',
                marginRight: '0'
              },
              specificAttrs: template?.specificAttrs || {
                title: 'Blank Card',
                content: 'Start from scratch',
                backgroundColor: '#ffffff',
                borderRadius: '12',
                borderWidth: '1',
                borderColor: '#E5E7EB',
                shadow: 'small',
                nestedComponents: []
              }
            };

            return (
              <button
                key={t.value}
                type="button"
                onClick={() => setSelectedKey(t.value)}
                onDoubleClick={() => { setSelectedKey(t.value); onSelectTemplate(t.value); }}
                style={{
                  textAlign: 'left',
                  padding: '12px',
                  borderRadius: '14px',
                  border: isSelected ? '2px solid rgba(255,255,255,0.95)' : '1px solid rgba(255,255,255,0.35)',
                  background: isSelected
                    ? 'linear-gradient(180deg, rgba(255,255,255,0.97), rgba(255,255,255,0.92))'
                    : 'linear-gradient(180deg, rgba(255,255,255,0.92), rgba(255,255,255,0.86))',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  boxShadow: isSelected
                    ? '0 18px 45px rgba(0,0,0,0.22)'
                    : '0 10px 30px rgba(0,0,0,0.18)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '800', color: '#111827' }}>
                    {t.label}
                  </div>
                  <input
                    type="radio"
                    checked={isSelected}
                    onChange={() => setSelectedKey(t.value)}
                    style={{ width: '16px', height: '16px', accentColor: '#3B82F6' }}
                  />
                </div>

                <div style={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid rgba(0,0,0,0.06)',
                  borderRadius: '14px',
                  padding: '10px',
                  overflow: 'hidden',
                  height: '180px',
                  maxHeight: '180px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden'
                  }}>
                    <div style={{ width: '100%', height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', minWidth: 0, minHeight: 0 }}>
                      <CardPreview
                        component={{ ...previewComponent, commonAttrs: { ...previewComponent.commonAttrs, height: '120', width: '100' } }}
                        theme={theme}
                        onClick={() => {}}
                        isSelected={false}
                      />
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '10px',
          padding: '14px 20px 18px',
          borderTop: '1px solid #E5E7EB',
          backgroundColor: '#FFFFFF'
        }}>
          <div style={{ fontSize: '12px', color: '#6B7280', fontWeight: '600' }}>
            Tip: double-click a template to create instantly
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 18px',
              fontSize: '13px',
              fontWeight: '700',
              color: '#6B7280',
              backgroundColor: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '10px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedKey}
            style={{
              padding: '10px 18px',
              fontSize: '13px',
              fontWeight: '800',
              color: '#FFFFFF',
              background: selectedKey
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : '#9CA3AF',
              border: 'none',
              borderRadius: '10px',
              cursor: selectedKey ? 'pointer' : 'not-allowed'
            }}
          >
            Create Card
          </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardTemplateModal;


